import React, { useState } from 'react';
import { toast } from 'sonner';
import { Mail, Phone, MessageCircle } from 'lucide-react';
import Navbar from '../../components/website/Navbar';
import Footer from '../../components/website/Footer';
import { apiPost } from '../../lib/api';
import { BUSINESS_TYPES, getIndustryTemplate } from '../../lib/industryTemplates';

export default function Contact() {
  const [form, setForm] = useState({ full_name: '', email: '', phone: '', business_name: '', merchant_type: 'general', customer_count: '', city: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const template = getIndustryTemplate(form.merchant_type);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const r = await apiPost('/contact', form);
      toast.success(r.message || 'Thanks — we will be in touch shortly.');
      setForm({ full_name: '', email: '', phone: '', business_name: '', merchant_type: 'general', customer_count: '', city: '', message: '' });
    } catch (err) {
      toast.error(err.message || 'Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const inputCls = 'w-full bg-white border border-gray-200 rounded-xl px-4 py-3 text-base text-rozops-text focus:outline-none focus:ring-2 focus:ring-rozops-blue/20 focus:border-rozops-blue transition-all';

  return (
    <div className="bg-rozops-bg min-h-screen">
      <Navbar />
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
        <div className="grid lg:grid-cols-5 gap-12">
          <div className="lg:col-span-2">
            <div className="inline-flex items-center gap-1.5 rounded-full bg-rozops-blue-light text-rozops-blue text-xs font-semibold px-3 py-1">Contact</div>
            <h1 className="mt-4 font-heading font-extrabold text-4xl sm:text-5xl text-rozops-navy tracking-tight">Let's simplify your daily business</h1>
            <p className="mt-4 text-lg text-rozops-text-muted">Tell us a little about your business. We'll get you set up in 15 minutes.</p>

            <div className="mt-8 space-y-3">
              {[
                { icon: Phone, title: 'Call us', v: '+91-9100507307', href: 'tel:+919100507307' },
                { icon: MessageCircle, title: 'WhatsApp', v: '+91-9100507307', href: 'https://wa.me/919100507307' },
                { icon: Mail, title: 'Email', v: 'hello@rozops.com', href: 'mailto:hello@rozops.com' },
              ].map(({ icon: Icon, title, v, href }) => (
                <a key={title} href={href} target={href.startsWith('http') ? '_blank' : undefined} rel="noreferrer" className="flex items-center gap-3 bg-white rounded-2xl border border-gray-100 shadow-soft p-4 hover:border-rozops-blue transition-colors" data-testid={`contact-${title.toLowerCase().replace(/\s/g, '-')}`}>
                  <div className="w-10 h-10 rounded-xl bg-rozops-blue-light text-rozops-blue grid place-items-center"><Icon className="w-5 h-5" /></div>
                  <div>
                    <div className="text-xs text-rozops-text-muted">{title}</div>
                    <div className="font-semibold text-rozops-navy">{v}</div>
                  </div>
                </a>
              ))}
            </div>
          </div>

          <form onSubmit={submit} className="lg:col-span-3 bg-white rounded-3xl border border-gray-100 shadow-soft p-6 sm:p-8 space-y-4" data-testid="contact-form">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-rozops-text mb-1.5">Your name</label>
                <input required data-testid="contact-name" className={inputCls} value={form.full_name} onChange={upd('full_name')} placeholder="Ramesh Kumar" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-rozops-text mb-1.5">Business name</label>
                <input className={inputCls} value={form.business_name} onChange={upd('business_name')} placeholder={template.exampleBusiness} />
              </div>
              <div>
                <label className="block text-sm font-semibold text-rozops-text mb-1.5">Email</label>
                <input required type="email" data-testid="contact-email" className={inputCls} value={form.email} onChange={upd('email')} placeholder="you@example.com" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-rozops-text mb-1.5">Phone</label>
                <input className={inputCls} value={form.phone} onChange={upd('phone')} placeholder="+91 90000 00000" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-rozops-text mb-1.5">Business type</label>
                <select className={inputCls} value={form.merchant_type} onChange={upd('merchant_type')}>
                  {BUSINESS_TYPES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-rozops-text mb-1.5">Customer count</label>
                <select className={inputCls} value={form.customer_count} onChange={upd('customer_count')}>
                  <option value="">Select</option>
                  <option>Under 25</option><option>25–100</option><option>100–300</option><option>300+</option>
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className="block text-sm font-semibold text-rozops-text mb-1.5">City</label>
                <input className={inputCls} value={form.city} onChange={upd('city')} placeholder="Mumbai" />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-sm font-semibold text-rozops-text mb-1.5">Message (optional)</label>
                <textarea rows={4} className={inputCls} value={form.message} onChange={upd('message')} placeholder="Tell us about your business…" />
              </div>
            </div>
            <button disabled={submitting} data-testid="contact-submit" className="w-full sm:w-auto bg-rozops-blue hover:bg-rozops-blue-dark text-white rounded-xl px-6 py-3 font-semibold shadow-soft hover:shadow-medium hover:-translate-y-0.5 transition-all disabled:opacity-50">
              {submitting ? 'Sending…' : 'Send message'}
            </button>
          </form>
        </div>
      </section>
      <Footer />
    </div>
  );
}
